import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { useAuth } from "@/lib/auth-context";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check, UploadSimple, X, LinkSimple, Plus, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";

// Quote-request intake — no live pricing. Vision Grafix returns a written quote after review.
const SERVICES = [
  { id: "flyer", label: "Flyer Design", sub: "Digital + print", supportsExtraSize: true },
  { id: "logo", label: "Logo Design", sub: "Custom marks", supportsExtraSize: false },
  { id: "seal", label: "Seal / Crest", sub: "Heraldic & ceremonial", supportsExtraSize: false },
  { id: "banner", label: "Banner Design", sub: "Web, social, print", supportsExtraSize: true },
  { id: "web", label: "Web Design", sub: "1-page → full site", supportsExtraSize: false },
  { id: "web_update", label: "Web Update", sub: "Quick changes", supportsExtraSize: false },
  { id: "other", label: "Other", sub: "Anything custom", supportsExtraSize: false },
];

const WEB_SUBTYPES = [
  { id: "business_1pager", label: "Business 1 Pager" },
  { id: "small_product_store", label: "Small Product Store" },
  { id: "large_product_store", label: "Large Product Store" },
  { id: "artist_booking_page", label: "Artist / Booking Page" },
  { id: "business_website", label: "Business Website" },
  { id: "church_website", label: "Church Website" },
];

const WEB_ADDONS = [
  { id: "seo_setup", label: "SEO Setup" },
  { id: "hosting_yearly", label: "Hosting (1 yr)" },
  { id: "maintenance_yearly", label: "Maintenance Plan (1 yr · free updates)" },
];

export default function RequestNew() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get("service") || "flyer";
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    service_type: SERVICES.find((s) => s.id === initialService) ? initialService : "flyer",
    title: "",
    description: "",
    web_subtype: "business_1pager",
    web_addons: [],
    extra_sizes: 0,
    sizes_detail: "",
    event_date: "",
    turnaround: "standard",
    target_date: "",
    design_preferences: "",
    reference_links: [""],
    budget_note: "",
  });

  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addLink = () => setForm((f) => ({ ...f, reference_links: [...f.reference_links, ""] }));
  const updateLink = (i, v) =>
    setForm((f) => ({ ...f, reference_links: f.reference_links.map((l, idx) => (idx === i ? v : l)) }));
  const removeLink = (i) =>
    setForm((f) => ({ ...f, reference_links: f.reference_links.filter((_, idx) => idx !== i) }));

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const selectedService = SERVICES.find((s) => s.id === form.service_type);
  const isWeb = form.service_type === "web";
  const isWebUpdate = form.service_type === "web_update";

  const minDateInfo = useMemo(() => {
    if (form.service_type === "flyer" || form.service_type === "banner") return "Specific date must be at least 72 hours out.";
    if (form.service_type === "web") return "Specific date must be at least 3 weeks out.";
    return "Specific date must be at least 7 days out.";
  }, [form.service_type]);

  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const cleanLinks = form.reference_links.map((l) => l.trim()).filter(Boolean);
      // Backend flags `payment_method="quote"` as a quote-request submission — total_usd=0,
      // status=submitted, payment_status=awaiting_quote. Admin returns a written quote later.
      const { data } = await api.post("/projects", {
        ...form,
        payment_method: "quote",
        reference_links: cleanLinks,
      });
      if (files.length) {
        const projectId = data.id;
        for (const f of files) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/projects/${projectId}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (_) {
            toast.error(`Could not upload ${f.name}`);
          }
        }
      }
      await refresh();
      toast.success("Brief submitted — you'll get a written quote by email.");
      navigate(`/projects/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels = ["Service", "Details", "Review"];

  return (
    <ClientLayout>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Quote request</div>
        <h1 className="font-display text-5xl md:text-6xl mt-2">Share your vision.</h1>
        <p className="text-sm text-[#a3a3a3] mt-3 max-w-2xl">
          Send us the brief and we'll return a written quote within one business day. No card details
          up front, no auto-charges — you approve every number before work starts.
        </p>
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-3 gap-2 mb-10" data-testid="stepper">
        {stepLabels.map((label, i) => {
          const idx = i + 1;
          const isActive = step === idx;
          const isDone = step > idx;
          return (
            <div
              key={label}
              className={`p-3 border ${
                isActive ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : isDone ? "border-[#262626] bg-[#0f0f0f]" : "border-[#262626] bg-[#0a0a0a] opacity-60"
              }`}
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">
                <span>{String(idx).padStart(2, "0")}</span>
                {isDone && <Check size={12} className="text-[#FF2D2D]" weight="bold" />}
              </div>
              <div className={`text-sm font-semibold mt-1 ${isActive ? "text-white" : "text-[#a3a3a3]"}`}>{label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-12 space-y-6 max-w-3xl">
          {/* STEP 1: Service */}
          {step === 1 && (
            <div data-testid="step-1">
              <h2 className="font-display text-3xl mb-6 uppercase">Pick a service</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {SERVICES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => update("service_type", s.id)}
                    data-testid={`select-service-${s.id}`}
                    className={`text-left p-5 border transition-colors ${
                      form.service_type === s.id ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626] bg-[#0a0a0a] hover:border-[#FF2D2D]/50"
                    }`}
                  >
                    <div className="font-display text-2xl">{s.label}</div>
                    <div className="text-xs text-[#a3a3a3] mt-1">{s.sub}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Details */}
          {step === 2 && (
            <div data-testid="step-2" className="space-y-5">
              <h2 className="font-display text-3xl mb-2 uppercase">Project details</h2>

              {isWebUpdate && user?.has_maintenance_plan && (
                <div className="border border-[#22c55e]/40 bg-[#22c55e]/5 p-3 text-xs text-[#22c55e]">
                  ✓ Your active maintenance plan covers this update.
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Project title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  data-testid="form-title"
                  placeholder="e.g. Spring sale flyer"
                  className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  {isWebUpdate ? "Describe your update" : "Vision / description"}
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  data-testid={isWebUpdate ? "form-update-description" : "form-description"}
                  rows={isWebUpdate ? 8 : 4}
                  placeholder={
                    isWebUpdate
                      ? "Tell us exactly what should change. Examples:\n• Replace homepage hero image\n• Update phone number in footer\n• Add new 'About' section under services\n• Swap pricing on Plans page"
                      : "Tell us what you need…"
                  }
                  className={`w-full bg-[#0a0a0a] border ${isWebUpdate ? "border-[#FF2D2D]" : "border-[#262626]"} px-4 py-3 focus:border-[#FF2D2D] outline-none`}
                />
                {isWebUpdate && (
                  <div className="text-xs text-[#a3a3a3] mt-2">
                    Be specific — include URLs of pages, exact wording for text changes, and what should be replaced. Attach screenshots & files below.
                  </div>
                )}
              </div>

              {isWeb && (
                <>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Website type</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {WEB_SUBTYPES.map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => update("web_subtype", w.id)}
                          data-testid={`web-subtype-${w.id}`}
                          className={`text-left p-3 border ${
                            form.web_subtype === w.id ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626] bg-[#0a0a0a]"
                          }`}
                        >
                          <div className="text-sm font-semibold">{w.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Website add-ons</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {WEB_ADDONS.map((a) => {
                        const checked = form.web_addons.includes(a.id);
                        return (
                          <label
                            key={a.id}
                            className={`flex items-center justify-between p-3 border cursor-pointer ${
                              checked ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626] bg-[#0a0a0a]"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={checked}
                              data-testid={`web-addon-${a.id}`}
                              onChange={(e) =>
                                update(
                                  "web_addons",
                                  e.target.checked
                                    ? [...form.web_addons, a.id]
                                    : form.web_addons.filter((x) => x !== a.id)
                                )
                              }
                            />
                            <span className="text-sm">{a.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {selectedService?.supportsExtraSize && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                      Extra sizes / versions
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.extra_sizes}
                      onChange={(e) => update("extra_sizes", e.target.value)}
                      data-testid="form-extra-sizes"
                      className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Size details</label>
                    <input
                      type="text"
                      value={form.sizes_detail}
                      onChange={(e) => update("sizes_detail", e.target.value)}
                      data-testid="form-sizes-detail"
                      placeholder="e.g. 4x6 + IG square"
                      className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Event date (optional)</label>
                <input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => update("event_date", e.target.value)}
                  data-testid="form-event-date"
                  className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-3">Turnaround</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { id: "standard", label: "Standard", desc: "Default" },
                    { id: "rush", label: "Rush", desc: "Fast-track priority" },
                    { id: "specific_date", label: "Specific date", desc: minDateInfo },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => update("turnaround", opt.id)}
                      data-testid={`turnaround-${opt.id}`}
                      className={`text-left p-4 border ${
                        form.turnaround === opt.id ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626] bg-[#0a0a0a]"
                      }`}
                    >
                      <div className="font-display text-xl">{opt.label}</div>
                      <div className="text-xs text-[#a3a3a3] mt-1">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {form.turnaround === "specific_date" && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Target date</label>
                  <input
                    type="date"
                    value={form.target_date}
                    onChange={(e) => update("target_date", e.target.value)}
                    data-testid="form-target-date"
                    className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Design preferences</label>
                <textarea
                  value={form.design_preferences}
                  onChange={(e) => update("design_preferences", e.target.value)}
                  data-testid="form-preferences"
                  rows={3}
                  placeholder="Color palette, vibe, references…"
                  className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  Budget or scope notes <span className="text-[#666] normal-case tracking-normal">(optional)</span>
                </label>
                <textarea
                  value={form.budget_note}
                  onChange={(e) => update("budget_note", e.target.value)}
                  data-testid="form-budget-note"
                  rows={2}
                  placeholder="e.g. 'Under $X for Q1', 'Government RFP #12345', or 'flexible — quote your best number'"
                  className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
                />
                <div className="text-xs text-[#a3a3a3] mt-1">
                  Helps us tailor the quote. Leave blank if you'd rather have us propose scope options.
                </div>
              </div>

              {/* Reference files */}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  Reference files <span className="text-[#666] normal-case tracking-normal">(optional, multiple)</span>
                </label>
                <label className="flex items-center justify-center gap-2 p-6 border-2 border-dashed border-[#262626] hover:border-[#FF2D2D] cursor-pointer text-[#a3a3a3] hover:text-white transition-colors">
                  <UploadSimple size={20} />
                  <span className="text-sm">Click to attach images, PDFs, or any source files</span>
                  <input
                    type="file"
                    multiple
                    onChange={onPickFiles}
                    data-testid="form-files"
                    className="hidden"
                  />
                </label>
                {files.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="form-files-list">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 bg-[#0a0a0a] border border-[#262626] text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <ImageSquare size={14} className="text-[#FF2D2D] shrink-0" />
                          <span className="truncate">{f.name}</span>
                          <span className="text-xs text-[#666] shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          data-testid={`remove-file-${i}`}
                          className="text-[#a3a3a3] hover:text-[#FF2D2D] shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reference links */}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  Reference links <span className="text-[#666] normal-case tracking-normal">(optional)</span>
                </label>
                <div className="space-y-2" data-testid="form-links">
                  {form.reference_links.map((link, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <LinkSimple size={14} className="text-[#FF2D2D] shrink-0" />
                      <input
                        type="url"
                        value={link}
                        onChange={(e) => updateLink(i, e.target.value)}
                        data-testid={`form-link-${i}`}
                        placeholder="https://figma.com/… or any inspiration link"
                        className="flex-1 bg-[#0a0a0a] border border-[#262626] px-3 py-2.5 text-sm focus:border-[#FF2D2D] outline-none"
                      />
                      {form.reference_links.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLink(i)}
                          data-testid={`remove-link-${i}`}
                          className="text-[#a3a3a3] hover:text-[#FF2D2D]"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addLink}
                    data-testid="add-link"
                    className="text-xs uppercase tracking-wider text-[#FF2D2D] hover:underline flex items-center gap-1"
                  >
                    <Plus size={12} weight="bold" /> Add another link
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Review */}
          {step === 3 && (
            <div data-testid="step-3" className="space-y-4">
              <h2 className="font-display text-3xl mb-2 uppercase">Review & submit</h2>
              <div className="border border-[#262626] bg-[#0a0a0a] p-5 space-y-3">
                <div className="flex justify-between"><span className="text-[#a3a3a3]">Service</span><span>{selectedService?.label}</span></div>
                <div className="flex justify-between"><span className="text-[#a3a3a3]">Title</span><span>{form.title || "—"}</span></div>
                <div className="flex justify-between"><span className="text-[#a3a3a3]">Turnaround</span><span className="capitalize">{form.turnaround.replace("_", " ")}</span></div>
                {form.target_date && <div className="flex justify-between"><span className="text-[#a3a3a3]">Target date</span><span>{form.target_date}</span></div>}
                {form.budget_note && <div className="flex justify-between gap-4"><span className="text-[#a3a3a3] shrink-0">Budget note</span><span className="text-right text-[#fafafa]">{form.budget_note}</span></div>}
                <div className="flex justify-between border-t border-[#262626] pt-3"><span className="text-[#a3a3a3]">Status after submit</span><span className="text-[#FF2D2D] uppercase text-xs tracking-wider font-bold">Awaiting quote</span></div>
              </div>
              <p className="text-xs text-[#a3a3a3]">
                We'll review your brief and email a written quote — no card required. Once you approve
                the quote in-portal, we start production.
              </p>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !form.title || !form.description}
                data-testid="submit-request"
                className="btn-primary w-full px-6 py-4 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Request my quote →"}
              </button>
            </div>
          )}

          <div className="flex items-center justify-between pt-6 border-t border-[#262626] mt-8">
            <button
              type="button"
              onClick={back}
              disabled={step === 1}
              data-testid="step-back"
              className="btn-outline px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-30"
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < 3 && (
              <button
                type="button"
                onClick={next}
                data-testid="step-next"
                className="btn-primary px-6 py-2.5 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
              >
                Continue <ArrowRight size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </ClientLayout>
  );
}
